import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, Form, FormFeedback, Input, Label, Row, Col } from 'reactstrap';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { instructorApi } from '../api/instructorApi';
import { fileUploadApi } from '../data/api/fileUpload';
import toast from 'react-hot-toast';
import { useFetchInstructors } from '../data/swrs/instructor';
import { mutate } from 'swr';

function AddInstructorModal({ isOpen, toggle, isEdit, instructor = {} }) {
	const [selectedImage, setSelectedImage] = useState(null);
	const [preview, setPreview] = useState(null);

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm({
		defaultValues: {
			name: instructor?.name || '',
			bio: instructor?.bio || '',
			imgUrl: null,
			socials: [{ platform: '', url: '' }]
		}
	});
	useEffect(() => {
		if (isEdit && instructor && instructor._id) {
			reset({
				name: instructor.name || '',
				bio: instructor.bio || '',
				imgUrl: instructor.imgUrl || null,
				socials: instructor.socials?.length ? instructor.socials : [{ platform: '', url: '' }]
			});
		}
	}, [isEdit, instructor?._id]);

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'socials'
	});


	const onNext = async (state) => {
		
		try {
			if (selectedImage) {
				const uploadResponse = await fileUploadApi(selectedImage);
				state.imgUrl = uploadResponse.data.cdnLink;
			}

			const formData = {
				...state
			};

			if (isEdit && instructor?._id) {
				const updateResponse = await instructorApi.updateInstructor(instructor._id, formData);

				if (updateResponse.status === 200) {
					toast.success('Instructor updated successfully!', {
						position: 'top-center',
						duration: 3000
					});
				}
			} else {
				const createResponse = await instructorApi.createInstructor(formData);
				toast.success('Instructor created successfully!', {
					position: 'top-center',
					duration: 3000
				});

			}
			mutate(useFetchInstructors.key);
		} catch (err) {
			console.log('Error:', err);
		} finally {
			toggle();
		}
	};

	const handleImageChange = e => {
		const file = e.target.files[0];
		if (file) {
			setSelectedImage(file);
			setPreview(URL.createObjectURL(file));
		}
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} centered backdrop="static" fade>
			<ModalHeader toggle={toggle}>{isEdit ? 'Edit Instructor' : 'Add Instructor'}</ModalHeader>
			<ModalBody>
				<Form onSubmit={handleSubmit(onNext)} encType="multipart/form-data">
					<Row>
					
						<Col md="12" className="form-group mb-2">
							<Controller
								name="name"
								control={control}
								rules={{ required: 'Name is required' }}
								render={({ field }) => (
									<Input
										placeholder="Enter Name"
										className={`form-control ${errors.name ? 'is-invalid' : ''}`}
										{...field}
										type="text"
									/>
								)}
							/>
							{errors.name && <FormFeedback>{errors.name.message}</FormFeedback>}
						</Col>

						<Col md="12" className="form-group mb-2">
							<Controller
								name="bio"
								control={control}
								render={({ field }) => <textarea placeholder="Enter bio" className="form-control" {...field} />}
							/>
						</Col>

						<Col md="12" className="form-group mb-2">
							<Label>Upload Image</Label>
							<Input type="file" accept="image/*" className="form-control" onChange={handleImageChange} />
							{preview && (
								<div className="mt-2">
									<img src={preview} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
								</div>
							)}
						</Col>

						<Col md="12" className="form-group mb-2">
							<Label>Social Links</Label>
							{fields.map((item, index) => (
								<div key={item.id} className="mb-3">
									<div className="d-flex align-items-start mb-2">
										<div className="flex-grow-1 me-2">
											<Controller
												control={control}
												name={`socials.${index}.platform`}
												rules={{ required: "Please select a platform" }}
												render={({ field }) => (
													<>
														<Input
															{...field}
															type="select"
															invalid={!!errors.socials?.[index]?.platform}
														>
															<option value="">Select Platform</option>
															<option value="Facebook">Facebook</option>
															<option value="Twitter">Twitter</option>
															<option value="LinkedIn">LinkedIn</option>
															<option value="Instagram">Instagram</option>
															<option value="Other">Other</option>
														</Input>
														{errors.socials?.[index]?.platform && (
															<FormFeedback>
																{errors.socials[index].platform.message}
															</FormFeedback>
														)}
													</>
												)}
											/>
										</div>

										<div className="flex-grow-1 me-2">
											<Controller
												control={control}
												name={`socials.${index}.url`}
												rules={{
													required: "URL is required",
													pattern: {
														value: /^https?:\/\/.+\..+/,
														message: 'Enter a valid URL (e.g., https://example.com)'
													}
												}}
												render={({ field }) => (
													<>
														<Input
															{...field}
															type="url"
															placeholder="Enter Social Link"
															invalid={!!errors.socials?.[index]?.url}
														/>
														{errors.socials?.[index]?.url && (
															<FormFeedback>
																{errors.socials[index].url.message}
															</FormFeedback>
														)}
													</>
												)}
											/>
										</div>

										{index > 0 && (
											<Button
												type="button"
												color="danger"
												size="sm"
												onClick={() => remove(index)}
											>
												Remove
											</Button>
										)}
									</div>
								</div>
							))}

							<Button
								type="button"
								color="secondary"
								outline
								onClick={() => append({ platform: '', url: '' })}
								className="mt-2"
							>
								+ Add More
							</Button>
						</Col>

						<Col className="d-flex justify-content-end">
							<Button type="submit" color="primary">
								{isEdit ? 'Update' : 'Submit'}
							</Button>
						</Col>
					</Row>
				</Form>
			</ModalBody>
		</Modal>
	);
}

export default AddInstructorModal;
